import { useMemo } from 'react'
import { Color, Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D } from 'three'
import * as TWEEN from '@tweenjs/tween.js'

import { animateChangeMaterials, TColorOption } from '../utils/animate-change-materials'

import { Configurator } from './Configurator/Configurator'
import { MaterialColor } from '../../../../trillion-common/src/objects/material-color.object'
import { useAppStore } from '../../shared/store/store'

export type Material = MeshBasicMaterial | MeshStandardMaterial

interface SelectColorProps {
  model: Object3D | undefined
  materialColors: MaterialColor[]
}

export const SelectColor = ({ model, materialColors }: SelectColorProps) => {
  const currentMaterialColor = useAppStore((state) => state.currentMaterialColor)
  const setCurrentMaterialColor = useAppStore((state) => state.setCurrentMaterialColor)

  let tween: TWEEN.Tween<TColorOption>

  const materialNames = useMemo(() => {
    const names = []
    for (const color of materialColors) {
      names.push(color.materialName)
    }

    return Array.from(new Set(names))
  }, [model])

  function changeColor(model: Object3D, currentIndex: number, prevIndex: number) {
    setCurrentMaterialColor(materialColors[currentIndex])

    if (tween) TWEEN.remove(tween)
    for (const group of model.children) {
      const materials: Array<Material> = []
      const prevColors: Array<Color> = []
      const currentColors: Array<Color> = []

      for (const mesh of group.children) {
        const material = (mesh as Mesh).material as Material
        materialNames.forEach((name, index) => {
          if (material.name === name) {
            materials.push(material)
            if (prevIndex > -1 && index === currentIndex) {
              prevColors.push(material.color)
            } else if (index === currentIndex) {
              currentColors.push(material.color)
            }
          }
        })
      }

      const current = {
        color: new Color(
          prevIndex > -1
            ? materialColors[prevIndex].color
            : ((group.children[0] as Mesh).material as Material).color,
        ),
      }
      const target = { color: new Color(materialColors[currentIndex].color) }
      tween = animateChangeMaterials(materials, current, target)
    }
  }

  return (
    <Configurator
      model={model}
      items={materialColors}
      currentItem={currentMaterialColor}
      title={'color'}
      onChange={changeColor}
    />
  )
}
